<template>
	<div class="amelipro-tabs">
		<div
			v-if="breakpointHelper.isDesktop() || pills"
			class="amelipro-tabs--desktop"
		>
			<div
				class="amelipro-tabs__btn-group"
				:class="btnGroupClasses"
			>
				<slot name="tabsDesc" />

				<div
					:id="id"
					:aria-label="ariaLabel"
					:aria-labelledby="ariaLabelledby"
					class="d-flex flex-column flex-sm-row align-center align-sm-end flex-wrap justify-center justify-sm-start amelipro-tabs__btn-group__wrapper"
					role="tablist"
				>
					<AmeliproTabBtn
						v-for="(item, index) in items"
						:key="index"
						ref="ameliproTabsBtns"
						:class="{
							'first-tab-btn': index === 0 && !pills,
							'last-tab-btn': index === items.length - 1 && !pills,
						}"
						:controls="`${id}-tab-panel-${index}`"
						:disabled="item.disabled"
						:notification="item.notification"
						:pills="pills"
						:selected="index === selected && !item.disabled"
						:tabindex="selected === index && !item.disabled ? 0 : -1"
						@click="onClick(index)"
						@keyup.end="pressEnd"
						@keyup.home="pressHome"
						@keyup.left="pressLeft"
						@keyup.right="pressRight"
					>
						{{ item.label }}
					</AmeliproTabBtn>
				</div>
			</div>

			<div
				v-for="(tab, index) in items"
				v-show="index === selected"
				:id="`${id}-tab-panel-${index}`"
				:key="index"
				class="tabpanel amelipro-tabs__tab"
				:class="{
					'ap-white pa-6': !noTabDefaultStyle,
					'tab-panel-default': !noTabDefaultStyle,
					'border-top-left': !noTabDefaultStyle && !pills,
				}"
				role="tabpanel"
				:tabindex="selected === index ? 0 : -1"
			>
				<slot :name="`${id}-tab-panel-${index}`" />
			</div>
		</div>

		<div
			v-else
			class="amelipro-tabs--mobile"
		>
			<div
				class="amelipro-tabs__btn-group"
				:class="btnGroupClasses"
			>
				<slot name="tabsDesc" />

				<AmeliproSelect
					:id="`${id}-select`"
					ref="ameliproTabsSelect"
					:aria-controls="`${id}-select-panel`"
					class="amelipro-tabs__select"
					horizontal
					:items="mobileItems"
					label="Selectionnez l'onglet à afficher"
					:value="mobileItems[selected]"
					@change="selectChange"
				/>
			</div>

			<div :id="`${id}-select-panel`">
				<div
					v-for="(tab, index) in items"
					v-show="index === selected"
					:id="`${id}-tab-panel-${index}`"
					:key="index"
					class=" tabpanel amelipro-tabs__tab"
					:class="{
						'ap-white pa-6 tab-panel-default': !noTabDefaultStyle,
					}"
					role="tabpanel"
					:tabindex="selected === index ? 0 : -1"
				>
					<slot :name="`${id}-tab-panel-${index}`" />
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { AmeliproTab, AmeliproTabsSelect } from './types';
	import { PropType, computed, onMounted, onUpdated, ref, watch } from 'vue';
	import AmeliproSelect from '../AmeliproSelect/AmeliproSelect.vue';
	import AmeliproTabBtn from '../AmeliproTabBtn';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { SelectItem } from '../AmeliproSelect/types';

	const props = defineProps({
		ariaLabel: {
			type: String,
			default: undefined,
		},
		ariaLabelledby: {
			type: String,
			default: undefined,
		},
		btnGroupClasses: {
			type: String,
			default: undefined,
		},
		id: {
			type: String,
			required: true,
		},
		items: {
			type: Array as PropType<AmeliproTab[]>,
			default: () => [],
		},
		noTabDefaultStyle: {
			type: Boolean,
			default: false,
		},
		pills: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Number,
			default: 0,
		},
	});

	const ameliproTabsBtns = ref<typeof AmeliproTabBtn[]>();
	const ameliproTabsSelect = ref<AmeliproTabsSelect>();
	const selected = ref(props.value);
	const selectedString = ref(selected.value.toString());
	const breakpointHelper = new BreakpointHelper();

	const mobileItems = computed<SelectItem[]>(() => {
		const selectList: SelectItem[] = [];
		props.items.forEach((element, index) => {
			let text = element.label;
			if (element.notification) {
				text += `(${element.notification})`;
			}
			selectList.push({
				disabled: element.disabled,
				text,
				value: index,
			});
		});
		return selectList;
	});

	onMounted(() => {
		setDefaultValues();
		hideSelectLabel();
	});

	onUpdated(() => {
		hideSelectLabel();
	});

	const hideSelectLabel = (): void => {
		document.getElementById(`${props.id}-select-label`)?.setAttribute('class', 'd-sr-only');
	};

	const setDefaultValues = (): void => {
		if (props.items !== undefined && selected.value !== undefined && props.items[selected.value] !== undefined) {
			let disabledBtn = props.items[selected.value].disabled;

			while (disabledBtn) {
				selected.value += 1;

				if (props.items[selected.value].disabled === false) {
					disabledBtn = false;
				}
			}
		}
	};

	const onClick = (index: number): void => {
		selected.value = index;
		emitTabChangeEvent();
	};

	const pressLeft = (): void => {
		let disabledBtn = true;
		if (selected.value === 0) {
			let tablePos = 1;

			while (disabledBtn) {
				selected.value = props.items.length - tablePos;
				if (props.items[selected.value].disabled === false) {
					disabledBtn = false;
				}
				tablePos += 1;
			}
			focusChange();
		}

		while (disabledBtn) {
			selected.value -= 1;

			if (selected.value < 0) {
				selected.value = props.items.length - 1;
			}

			if (props.items[selected.value].disabled === false) {
				disabledBtn = false;
			}
			focusChange();
		}
	};

	const pressRight = (): void => {
		let disabledBtn = true;
		if (selected.value === (props.items.length - 1)) {
			let tablePos = 0;

			while (disabledBtn) {
				selected.value = tablePos;
				if (props.items[selected.value].disabled === false) {
					disabledBtn = false;
				}
				tablePos += 1;
			}
			focusChange();
		}

		while (disabledBtn) {
			selected.value += 1;

			if (selected.value === props.items.length) {
				selected.value = 0;
			}

			if (props.items[selected.value].disabled === false) {
				disabledBtn = false;
			}
			focusChange();
		}
	};

	const pressHome = (): void => {
		selected.value = 0;
		let disabledBtn = props.items[selected.value].disabled;

		while (disabledBtn) {
			selected.value += 1;

			if (props.items[selected.value].disabled === false) {
				disabledBtn = false;
			}
		}
		focusChange();
	};

	const pressEnd = (): void => {
		selected.value = props.items.length - 1;
		let disabledBtn = props.items[selected.value].disabled;

		while (disabledBtn) {
			selected.value -= 1;

			if (props.items[selected.value].disabled === false) {
				disabledBtn = false;
			}
		}
		focusChange();
	};

	const focusChange = (): void => {
		if (ameliproTabsBtns.value) {
			(ameliproTabsBtns.value[selected.value].$el as HTMLElement).focus();
			emitTabChangeEvent();
		}
	};

	const selectChange = (): void => {
		selected.value = Number(ameliproTabsSelect.value?.inputValue);
		selectedString.value = selected.value.toString();
		emitTabChangeEvent();
	};

	const emit = defineEmits(['change-tab']);
	const emitTabChangeEvent = (): void => {
		emit('change-tab', selected.value);
	};

	watch(() => props.value, () => {
		selected.value = props.value;
	});
</script>

<style lang="scss" scoped>
.first-tab-btn {
	border-top-left-radius: 8px;
}

.last-tab-btn {
	border-top-right-radius: 8px;
}

.tabpanel {
	&:focus {
		outline: 1px dotted $ap-grey-darken1;
	}
}

.tab-panel-default {
	border-radius: 8px;
	border: 1px solid $ap-grey-lighten2;
}

.border-top-left {
	@media #{$media-up-md} {
		border-top-left-radius: 0;
	}
}
</style>
